/* prettier-ignore */

import { Component, computed, inject, Input, signal, ViewChild, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SwiperComponent, SwiperModule } from "swiper/angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SharedModule } from "src/app/shared/shared.module";
import { CommonModule } from '@angular/common';
import { RegisterStandaloneActionButtonsComponent } from "./action-buttons/action-buttons.component";
import { RegisterUserStandaloneDataComponent } from "./step-user-data/step-user-data.component";
import { RegisterStandaloneService } from './register-standalone.service';
import { catchError, map, of, throwError } from "rxjs";
import { ToolsService } from "src/app/services/tools.service";
import { RestService } from "src/app/services/rest.service";
import SwiperCore, { Autoplay, EffectFade, Pagination } from "swiper";
import Swal from "sweetalert2";

SwiperCore.use([EffectFade, Autoplay, Pagination]);

@Component({
  selector: "register-user-standalone",
  templateUrl: "./register-standalone.page.html",
  styleUrls: ["./register-standalone.page.scss"],
  standalone: true,
  imports: [
    CommonModule, 
    SharedModule,
    TranslateModule,
    SwiperModule,
    RegisterStandaloneActionButtonsComponent,
    RegisterUserStandaloneDataComponent
  ],
  encapsulation: ViewEncapsulation.None,
})

export class RegisterStandaloneComponent {
  @ViewChild("swiperRegisterStandalone", { static: false })  swiperRegisterStandalone?: SwiperComponent;
  @Input('registerOptions') registerOptions?: any;
  private registerStandaloneService = inject(RegisterStandaloneService);
  private tools = inject(ToolsService);
  private rest = inject(RestService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  public translate = inject(TranslateService);

  private currentSwiperIndex = signal(0);
  private isCorporativeColorsLoaded = computed(() => this.tools.isCorporativeColorsLoaded())
  public isFirstSlide = computed(() => this.currentSwiperIndex() === 0);
  public isLastSlide = computed(() => this.currentSwiperIndex() === this.swiperRegisterStandalone?.swiperRef?.slides?.length - 1);
  public techOptions: any;
  private id;
  private token;

  constructor() {
    this.tools.isRegisterMode.set(true);
  }

  ngOnInit(){
    this.id = this.route.snapshot.paramMap.get('id');
    this.token = this.route.snapshot.paramMap.get('token');

    if (this.token){
      this.getDataByToken(this.token);
      return;
    }

    if (this.id){
      this.getDataById(this.id);
      return;
    }
  }

  getDataById(id){
    this.registerStandaloneService.getRegisterDataStandaloneById(id)
    .pipe(
      catchError((error) => {
        this.tools.disableHeader();
        this.router.navigateByUrl("not-found");
        return throwError(() => new Error('HTTP request failed', { cause: error }));
      }),
      map((response) => {
        this.rest.addThumbnail(response, "w200");
        return response;
      })
    )
    .subscribe((res: any) => {
      this.setCorporativeColors(res);
      this.setTechData(res);
    })
  }

  getDataByToken(token){
    this.registerStandaloneService.getRegisterDataStandaloneByToken(token)
    .pipe(
      catchError((error) => {
        this.tools.disableHeader();
        this.router.navigateByUrl("not-found");
        return throwError(() => new Error('HTTP request failed', { cause: error }));
      }),
      map((response) => {
        this.rest.addThumbnail(response, "w200");
        return response;
      })
    )
    .subscribe((res: any) => {
      this.registerStandaloneService.clientId = res?.clientID;
      this.registerStandaloneService.channelId = res?.id;
      this.setCorporativeColors(res);
      this.tools.setLanguages(res?.lang?.languages);
      this.setTechData(res);
    })
  }

  setCorporativeColors(response){
    if (this.isCorporativeColorsLoaded())
      return;

    this.rest.setDevice(response);
    this.tools.setCorporativeColors();
    this.tools.enableHeader();
  }

  setTechData(response){
    if (response.status === 'Error'){
      this.tools.disableHeader();
      this.router.navigateByUrl("not-found");
      return;
    }
    const {travelerType, categories, subCategories, registers, countries, provinces, survey} = response;
    this.registerStandaloneService.registerBeforeQuestion.set(registers?.registerBefore);
    this.registerStandaloneService.registerAfterQuestion.set(registers?.registerAfter);
    this.registerStandaloneService.surveyQuestion.set(survey?.data);
    this.techOptions = {
      travelerType, 
      categories, 
      subCategories, 
      countries, 
      provinces,
      registerBefore: registers?.registerBefore,
      registerAfter: registers?.registerAfter,
      survey: survey?.data
    };
  }

  swiperNavigate(event){
    if (event === 'next') this.nextStep();
    if (event === 'back') this.goBack();
  }

  handleReceivedData(event, type){
    switch(type){
      case 'calendar': this.registerStandaloneService.setCalendarData(event); break;
      case 'travelerType': this.registerStandaloneService.setTravelerType(event); break;
      case 'interestType': this.registerStandaloneService.setCategories(event); break;
      case 'registerBefore': this.registerStandaloneService.setRegisterBeforeAnswer(event); break;
      case 'registerAfter': this.registerStandaloneService.setRegisterAfterAnswer(event); break;
    }
  }

  nextStep() {
    const actualIndex = this.swiperRegisterStandalone?.swiperRef?.realIndex;
    const slideId = this.swiperRegisterStandalone?.swiperRef?.slides?.[actualIndex]?.children?.[0]?.id;

    if (slideId === 'travelerType' && this.registerStandaloneService?.isTravelerTypeError()) {
      this.showErrorMsg('Seleccione tipo de viajero');
      return;
    }

    if (slideId === 'interestType' && this.registerStandaloneService?.isInterestTypeError()) {
      this.showErrorMsg('Seleccione alguna categoria');
      return;
    }

    if (slideId === 'registerBefore' && this.registerStandaloneService?.isRegisterBeforeError()) {
      this.registerStandaloneService?.showRegisterBeforeError?.update(prev => true);
      this.showErrorMsg('Hay preguntas sin responder')
      return;
    }

    if (slideId === 'registerBefore')
      this.registerStandaloneService?.showRegisterBeforeError?.update(prev => false);

    if (slideId === 'registerAfter' && this.registerStandaloneService?.isRegisterAfterError()) {
      this.registerStandaloneService?.showRegisterAfterError?.update(prev => true);
      this.showErrorMsg('Hay preguntas sin responder');
      return;
    }

    if (slideId === 'registerAfter')
      this.registerStandaloneService?.showRegisterAfterError?.update(prev => false);

    if (!this.swiperRegisterStandalone.swiperRef.isEnd) {
      this.currentSwiperIndex.update(prev => prev + 1);
      this.swiperRegisterStandalone.swiperRef.slideNext();
      return;
    }
  }

  goBack() {
    if (this.swiperRegisterStandalone.swiperRef.realIndex === 0) 
      location.reload();

    this.currentSwiperIndex.update(prev => prev - 1);
    this.swiperRegisterStandalone.swiperRef.slidePrev();
  }

  showErrorMsg(text){
    Swal.fire("Oops...", this.translate.instant(text), "warning");
  }

}
